import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Tag, HelpCircle } from 'lucide-react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

function TemplateVariables() {
  const [variables, setVariables] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    default_value: ''
  });
  const [loading, setLoading] = useState(false);
const navigate = useNavigate();

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      const res = await api.get('/api/template-variables');
      setVariables(res.data);
    } catch (err) {
      console.error('Erro ao carregar variáveis:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editing) {
        await api.put(`/api/template-variables/${editing.id}`, form);
      } else {
        await api.post('/api/template-variables', form);
      }
      
      setShowModal(false);
      setEditing(null);
      setForm({ name: '', description: '', default_value: '' });
      loadVariables();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (variable) => {
    setEditing(variable);
    setForm({
      name: variable.name,
      description: variable.description || '',
      default_value: variable.default_value || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta variável?')) return;
    
    try {
      await api.delete(`/api/template-variables/${id}`);
      loadVariables();
    } catch (err) {
      alert('Erro ao excluir: ' + err.message);
    }
  };

  const handleCopy = (name) => {
    navigator.clipboard.writeText(`{{${name}}}`);
    alert(`Variável {{${name}}} copiada!`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
    <div className="flex items-center justify-between">
  <div className="flex items-center gap-4">
    {/* Botão Voltar */}
    <button
      onClick={() => navigate('/templates')}
      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all"
    >
      <ArrowLeft size={18} />
      Voltar para Templates
    </button>
    
    <div>
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
        <Tag size={28} className="text-purple-600" />
        Variáveis de Template
      </h1>
      <p className="text-gray-500">Gerencie variáveis dinâmicas para usar em mensagens</p>
    </div>
  </div>
  
  <button
    onClick={() => {
      setEditing(null);
      setForm({ name: '', description: '', default_value: '' });
      setShowModal(true);
    }}
    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
  >
    <Plus size={18} />
    Nova Variável
  </button>
</div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-900">
          <strong>💡 Como usar:</strong> Crie variáveis como {'{{nome_cliente}}'}, {'{{data_agendamento}}'}, etc.
          Elas serão substituídas automaticamente ao enviar mensagens.
        </p>
      </div>

      {/* Lista de Variáveis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {variables.map(variable => (
          <div key={variable.id} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <code className="text-sm font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded">
                  {'{{' + variable.name + '}}'}
                </code>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleCopy(variable.name)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Copiar variável"
                >
                  <Tag size={16} />
                </button>
                <button
                  onClick={() => handleEdit(variable)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(variable.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            {variable.description && (
              <p className="text-xs text-gray-600 mb-2 flex items-start gap-1">
                <HelpCircle size={12} className="flex-shrink-0 mt-0.5" />
                {variable.description}
              </p>
            )}
            
            {variable.default_value && (
              <p className="text-xs text-gray-500">
                <strong>Padrão:</strong> {variable.default_value}
              </p>
            )}
          </div>
        ))}
        
        {variables.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Tag size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Nenhuma variável cadastrada</p>
            <p className="text-sm mt-1">Crie sua primeira variável para começar</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold">
                {editing ? 'Editar Variável' : 'Nova Variável'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Variável *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value.replace(/\s/g, '_').toLowerCase()})}
                  placeholder="ex: nome_cliente"
                  required
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use letras minúsculas e underline (ex: nome_cliente)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="ex: Nome completo do cliente"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Padrão
                </label>
                <input
                  type="text"
                  value={form.default_value}
                  onChange={e => setForm({...form, default_value: e.target.value})}
                  placeholder="ex: Cliente"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Valor usado quando não houver dados específicos
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 disabled:opacity-50"
                >
                  <Save size={16} />
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateVariables;